import React, { useState, useEffect } from 'react';
import { Bell, CircleCheck as CheckCircle, Circle as XCircle, Clock, Trash2, Check, Filter, CreditCard as Edit2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  transaction_id?: string;
  installment_id?: string;
  contract_id?: string;
  metadata: {
    client_name?: string;
    amount?: number;
    rejection_reason?: string;
    rejected_by_email?: string;
    request_id?: string;
    seller_name?: string;
    reviewed_by?: string;
    changes_count?: number;
  };
}

export default function Notifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'unread' | 'read'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'payment_rejected' | 'payment_approved' | 'client_edit_request' | 'client_edit_approved' | 'client_edit_rejected'>('all');

  useEffect(() => {
    if (user) {
      fetchNotifications();
    }
  }, [user, filter, typeFilter]);

  const fetchNotifications = async () => {
    if (!user) return;

    setLoading(true);
    try {
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (filter === 'unread') {
        query = query.eq('is_read', false);
      } else if (filter === 'read') {
        query = query.eq('is_read', true);
      }

      if (typeFilter !== 'all') {
        query = query.eq('type', typeFilter);
      }

      const { data, error } = await query;

      if (error) throw error;

      setNotifications(data || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user?.id)
        .eq('is_read', false);

      if (error) throw error;

      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('id', notificationId);

      if (error) throw error;

      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    markAsRead(notification.id);
    if (notification.type === 'client_edit_request') {
      navigate('/dashboard/client-edit-review');
    } else {
      navigate('/dashboard/collections-v2');
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'payment_rejected':
        return <XCircle className="w-6 h-6 text-red-500" />;
      case 'payment_approved':
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case 'client_edit_request':
        return <Edit2 className="w-6 h-6 text-orange-500" />;
      case 'client_edit_approved':
        return <CheckCircle className="w-6 h-6 text-teal-500" />;
      case 'client_edit_rejected':
        return <XCircle className="w-6 h-6 text-red-500" />;
      default:
        return <Clock className="w-6 h-6 text-yellow-500" />;
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  if (!user) {
    return null;
  }

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Bell className="w-8 h-8 text-blue-600" />
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Notificações</h1>
                  {unreadCount > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      {unreadCount} {unreadCount === 1 ? 'não lida' : 'não lidas'}
                    </p>
                  )}
                </div>
              </div>

              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                >
                  <Check className="w-4 h-4" />
                  Marcar todas como lidas
                </button>
              )}
            </div>

            <div className="flex gap-4 mt-4 flex-wrap">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Status:</span>
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as any)}
                  className="text-sm border border-gray-300 rounded-md px-3 py-1"
                >
                  <option value="all">Todas</option>
                  <option value="unread">Não lidas</option>
                  <option value="read">Lidas</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Tipo:</span>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value as any)}
                  className="text-sm border border-gray-300 rounded-md px-3 py-1"
                >
                  <option value="all">Todos</option>
                  <option value="payment_rejected">Pagamentos Recusados</option>
                  <option value="payment_approved">Pagamentos Aprovados</option>
                  <option value="client_edit_request">Solicitacoes de Edicao</option>
                  <option value="client_edit_approved">Edicoes Aprovadas</option>
                  <option value="client_edit_rejected">Edicoes Recusadas</option>
                </select>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {loading ? (
              <div className="p-12 text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Carregando notificações...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <Bell className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">Nenhuma notificação encontrada</p>
                <p className="text-sm mt-2">
                  {filter !== 'all' || typeFilter !== 'all'
                    ? 'Tente ajustar os filtros acima'
                    : 'Você não tem notificações no momento'}
                </p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-6 hover:bg-gray-50 transition-colors ${
                    !notification.is_read ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                >
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {notification.title}
                            </h3>
                            {!notification.is_read && (
                              <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                                Nova
                              </span>
                            )}
                          </div>

                          <p className="text-sm text-gray-600 mt-2">
                            {notification.message}
                          </p>

                          {notification.metadata?.client_name && (
                            <p className="text-sm text-gray-900 mt-2">
                              <span className="font-medium">Cliente:</span>{' '}
                              {notification.metadata.client_name}
                            </p>
                          )}

                          {notification.metadata?.amount && (
                            <p className="text-sm text-gray-900 mt-1">
                              <span className="font-medium">Valor:</span>{' '}
                              {formatCurrency(notification.metadata.amount)}
                            </p>
                          )}

                          {notification.metadata?.rejection_reason && (
                            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                              <p className="text-sm font-medium text-red-900">
                                Motivo da Recusa:
                              </p>
                              <p className="text-sm text-red-800 mt-1">
                                {notification.metadata.rejection_reason}
                              </p>
                              {notification.metadata?.rejected_by_email && (
                                <p className="text-xs text-red-700 mt-2">
                                  Por: {notification.metadata.rejected_by_email}
                                </p>
                              )}
                              {notification.metadata?.reviewed_by && (
                                <p className="text-xs text-red-700 mt-2">
                                  Por: {notification.metadata.reviewed_by}
                                </p>
                              )}
                            </div>
                          )}

                          {notification.type === 'client_edit_request' && notification.metadata?.seller_name && (
                            <div className="mt-2 p-2.5 bg-orange-50 border border-orange-200 rounded-lg">
                              <p className="text-xs text-orange-800">
                                <span className="font-semibold">Vendedor:</span> {notification.metadata.seller_name}
                                {notification.metadata.changes_count && (
                                  <span className="ml-2 text-orange-600">· {notification.metadata.changes_count} campo{notification.metadata.changes_count !== 1 ? 's' : ''} alterado{notification.metadata.changes_count !== 1 ? 's' : ''}</span>
                                )}
                              </p>
                            </div>
                          )}

                          <p className="text-xs text-gray-500 mt-3">
                            {formatDateTime(notification.created_at)}
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          {!notification.is_read && (
                            <button
                              onClick={() => markAsRead(notification.id)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Marcar como lida"
                            >
                              <Check className="w-5 h-5" />
                            </button>
                          )}
                          <button
                            onClick={() => deleteNotification(notification.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Excluir"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>

                      <button
                        onClick={() => handleNotificationClick(notification)}
                        className="mt-4 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors inline-flex items-center gap-2"
                      >
                        {notification.type === 'client_edit_request' ? 'Revisar Solicitacao' : 'Ver Parcela'}
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
